/**
 * Call Signaling Service
 * Firestore-backed real-time call signaling with WebRTC support
 */

import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';

// ═══════════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════════

export type CallStatus = 'ringing' | 'active' | 'ended' | 'declined' | 'missed';
export type CallMode = 'ring' | 'silent' | 'flash';

export interface CallDoc {
  id: string;
  callerId: string;
  callerName: string;
  callerAvatar?: string;
  receiverId: string;
  receiverName: string;
  receiverAvatar?: string;
  type: 'audio' | 'video';
  status: CallStatus;
  roomId?: string;       // chat room where the trace message should be posted
  startedAt: any;        // serverTimestamp
  answeredAt?: any;
  endedAt?: any;
  endedBy?: string;      // which user ended it
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Create / Answer / Decline / End
// ═══════════════════════════════════════════════════════════════════════════════

/** Caller initiates a call → writes a doc with status 'ringing' */
export async function initiateCall(params: {
  callerId: string;
  callerName: string;
  callerAvatar?: string;
  receiverId: string;
  receiverName: string;
  receiverAvatar?: string;
  type: 'audio' | 'video';
  roomId?: string;
}): Promise<string> {
  const callRef = doc(collection(db, 'calls'));
  const callDoc: Omit<CallDoc, 'id'> = {
    callerId: params.callerId,
    callerName: params.callerName,
    callerAvatar: params.callerAvatar || '',
    receiverId: params.receiverId,
    receiverName: params.receiverName,
    receiverAvatar: params.receiverAvatar || '',
    type: params.type,
    status: 'ringing',
    ...(params.roomId ? { roomId: params.roomId } : {}),
    startedAt: serverTimestamp(),
  };
  await setDoc(callRef, callDoc);
  return callRef.id;
}

/** Receiver answers the call */
export async function answerCall(callId: string): Promise<void> {
  await updateDoc(doc(db, 'calls', callId), {
    status: 'active',
    answeredAt: serverTimestamp(),
  });
}

/** Either party declines / rejects */
export async function declineCall(callId: string, userId: string): Promise<void> {
  await updateDoc(doc(db, 'calls', callId), {
    status: 'declined',
    endedAt: serverTimestamp(),
    endedBy: userId,
  });
}

/** Either party ends an active call */
export async function endCall(callId: string, userId: string): Promise<void> {
  await updateDoc(doc(db, 'calls', callId), {
    status: 'ended',
    endedAt: serverTimestamp(),
    endedBy: userId,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Listeners
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Subscribe to an active call doc (for both caller and receiver).
 * Returns an unsubscribe function.
 */
export function subscribeCall(
  callId: string,
  callback: (callDoc: CallDoc | null) => void,
): () => void {
  return onSnapshot(doc(db, 'calls', callId), (snap) => {
    if (!snap.exists()) { callback(null); return; }
    callback({ id: snap.id, ...snap.data() } as CallDoc);
  });
}

/**
 * Subscribe to incoming calls for a specific user (status === 'ringing').
 * This is how the receiver detects an incoming call.
 */
export function subscribeIncomingCalls(
  userId: string,
  callback: (calls: CallDoc[]) => void,
): () => void {
  const q = query(
    collection(db, 'calls'),
    where('receiverId', '==', userId),
    where('status', '==', 'ringing'),
  );
  return onSnapshot(q, (snap) => {
    const calls = snap.docs.map((d) => ({ id: d.id, ...d.data() } as CallDoc));
    callback(calls);
  });
}

/**
 * Auto-expire stale "ringing" calls after a timeout (e.g. 30 seconds).
 * Call this periodically or use it with the subscribe to check timestamps.
 */
export function isCallExpired(call: CallDoc, timeoutMs = 30000): boolean {
  if (call.status !== 'ringing') return false;
  const startedAt = call.startedAt;
  if (!startedAt) return false;
  const ts = startedAt instanceof Timestamp ? startedAt.toMillis() : Date.now();
  return Date.now() - ts > timeoutMs;
}

/** Mark an expired ringing call as "missed" */
export async function markCallMissed(callId: string): Promise<void> {
  await updateDoc(doc(db, 'calls', callId), {
    status: 'missed',
    endedAt: serverTimestamp(),
  });
}

/**
 * Subscribe to any active call where the user is either the caller or receiver.
 * Used to mount the global ActiveCallUI regardless of which page the user is on.
 * Returns an unsubscribe function.
 */
export function subscribeActiveCallForUser(
  userId: string,
  callback: (call: CallDoc | null) => void,
): () => void {
  let callerCall: CallDoc | null = null;
  let receiverCall: CallDoc | null = null;

  const notify = () => callback(callerCall ?? receiverCall);

  const callerQ = query(
    collection(db, 'calls'),
    where('callerId', '==', userId),
    where('status', '==', 'active'),
  );

  const receiverQ = query(
    collection(db, 'calls'),
    where('receiverId', '==', userId),
    where('status', '==', 'active'),
  );

  const unsubCaller = onSnapshot(callerQ, (snap) => {
    callerCall = snap.docs.length > 0
      ? ({ id: snap.docs[0].id, ...snap.docs[0].data() } as CallDoc)
      : null;
    notify();
  });

  const unsubReceiver = onSnapshot(receiverQ, (snap) => {
    receiverCall = snap.docs.length > 0
      ? ({ id: snap.docs[0].id, ...snap.docs[0].data() } as CallDoc)
      : null;
    notify();
  });

  return () => {
    unsubCaller();
    unsubReceiver();
  };
}
